"use client";

import { useEffect, useState } from "react";
import { GripVertical, Plus, TrashIcon } from "lucide-react";

import { defaultNodeTableColumns } from "~/stores/setting";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui";
import { Dialog } from "~/components/common/Dialog";
import { ComboBox } from "~/components/common/combo-box";
import type { NodeTableColumnSettings } from "~/types/ui";

export function NodeTableColumnFormField({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (val: string) => void;
  options: { label: string; value: NodeTableColumnSettings[] }[];
}) {
  const { t } = useTranslation();
  const [dragging, setDragging] = useState<string | null>(null);
  const [allNodeTableColumns, setAllNodeTableColumns] = useState<
    NodeTableColumnSettings[]
  >([]);
  const [currentCols, setCurrentCols] = useState<NodeTableColumnSettings[]>([]);
  const [dialog, setDialog] = useState(false);

  // The company attributes gets passed down from the parent component,
  // because we reuse the information for other parts of the form
  const companyAttributes = options[0]?.value || [];

  function handleDragStart(col: string | undefined) {
    if (!col) return;

    setDragging(col);
  }

  function handleDragOver(
    e: React.DragEvent<HTMLDivElement>,
    overCol: string | undefined,
  ) {
    e.preventDefault(); // allow drop

    if (!overCol) return;
    if (!dragging || dragging === overCol) return;

    const currentIndex = currentCols.findIndex((d) => d.key === dragging);
    const overIndex = currentCols.findIndex((d) => d.key === overCol);
    if (currentIndex === -1 || overIndex === -1) return;

    let newOrder = [...currentCols];
    let val = newOrder.splice(currentIndex, 1); // remove dragged col
    if (val.length === 0 || val.length > 2)
      console.error("Unexpected delted two elements in array");

    newOrder.splice(overIndex, 0, val[0]); // insert before target

    newOrder = newOrder.map((c, i) => ({ ...c, index: i }));

    setCurrentCols(newOrder);
  }

  function handleDragEnd() {
    onChange(JSON.stringify(currentCols));
    setDragging(null);
  }

  function handleDelete(e: React.MouseEvent, colKey: string) {
    e.stopPropagation();
    const newCols = currentCols.filter((c) => c.key !== colKey);
    setCurrentCols(newCols);
    onChange(JSON.stringify(newCols));
  }

  const ignoreKeys = ["select", "dataPoints", "count", "alarm"];
  useEffect(() => {
    // Parse the value as json array
    try {
      const parsed: NodeTableColumnSettings[] = JSON.parse(value);
      if (parsed)
        setCurrentCols(parsed.filter((c) => !ignoreKeys.includes(c.key)));
    } catch {
      // Ignore
      setCurrentCols(
        defaultNodeTableColumns.filter((c) => !ignoreKeys.includes(c.key)),
      );
    }
  }, [value]);

  useEffect(() => {
    setAllNodeTableColumns(
      [...defaultNodeTableColumns, ...companyAttributes].filter(
        (d) => !ignoreKeys.includes(d.key),
      ),
    );
  }, [companyAttributes]);

  return (
    <div className="flex gap-2 overflow-x-auto">
      {currentCols.map((col, i) => (
        <DraggableColumnHeader
          key={col.key + i}
          column={col}
          dragging={dragging}
          onDragStart={() => handleDragStart(col.key)}
          onDragOver={(e) => handleDragOver(e, col.key)}
          onDragEnd={() => handleDragEnd()}
          onDelete={handleDelete}
        />
      ))}
      <Button
        type="button"
        className="flex py-1 px-2 rounded-lg bg-green-600 hover:bg-green-700 text-white"
        onClick={() => {
          setDialog(!dialog);
        }}
      >
        <Plus className="h-4 w-4" />
      </Button>

      <Dialog
        open={dialog}
        onOpenChange={setDialog}
        title={t("settings.nodeTable.addColumn")}
        children={
          <ComboBox
            options={allNodeTableColumns
              .filter(
                (d) => currentCols.findIndex((c) => c.key === d.key) === -1,
              )
              .map((c) => ({
                label:
                  c.useLangLine && c.langLine ? t(c.langLine) : (c.title ?? ""),
                value: c.key,
                category: c.isAttribute
                  ? t("settings.nodeTable.category.attribute")
                  : t("settings.nodeTable.category.default"),
              }))}
            onChange={(val) => {
              if (!val || currentCols.findIndex((c) => c.key === val) !== -1)
                return;

              let foundCol = allNodeTableColumns.find((c) => c.key === val);
              if (foundCol === undefined) return;

              let newCols = [...currentCols, foundCol];
              newCols = newCols.map((c, i) => ({ ...c, index: i }));

              setCurrentCols(newCols);
              onChange(JSON.stringify(newCols));
              setDialog(false);
            }}
          />
        }
      />
    </div>
  );
}

function DraggableColumnHeader({
  column,
  dragging,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDelete,
}: {
  column: NodeTableColumnSettings;
  dragging: string | null;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  onDelete: (e: React.MouseEvent, colKey: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDragEnd}
      className={`flex items-center gap-1 px-2 py-1 rounded-lg border bg-background cursor-grab text-sm no-wrap ${
        dragging === column.key ? "opacity-50" : ""
      }`}
    >
      <GripVertical className="h-4 w-4 text-muted-foreground" />
      <h3 className="whitespace-nowrap">
        {column.useLangLine && column.langLine
          ? t(column.langLine)
          : column.title}
      </h3>
      <TrashIcon
        className="ml-2 h-3 w-3 text-red-600 hover:text-red-800 cursor-pointer"
        onClick={(e) => onDelete(e, column.key)}
      />
    </div>
  );
}
